/**
 * Client telemetry helpers — roaming history and speed test (demo + API-backed).
 */

import type { ClientRoamingEvent, ClientSpeedTestResult, PlacedAp } from './types'

function hashStr(s: string) {
  let h = 0
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0
  return h
}

function isoMinutesAgo(minutes: number) {
  return new Date(Date.now() - minutes * 60_000).toISOString()
}

/** Deterministic demo roaming trail ending at the client's current AP. */
export function generateRoamingHistory(
  clientId: string,
  currentAp: string,
  aps: PlacedAp[],
  currentRssi: number,
): ClientRoamingEvent[] {
  const others = aps.map(a => a.ap).filter(name => name !== currentAp)
  const h = Math.abs(hashStr(clientId))
  const eventCount = 2 + (h % 3)
  const events: ClientRoamingEvent[] = []

  let minutesAgo = 90 + (h % 120)
  let prevAp = others.length > 0 ? others[h % others.length] : currentAp

  for (let i = eventCount - 1; i >= 0; i--) {
    const isLast = i === 0
    const ap = isLast ? currentAp : i === eventCount - 1 ? prevAp : others[(h + i) % Math.max(1, others.length)] ?? currentAp
    const rssi = isLast
      ? currentRssi
      : Math.round(currentRssi - 8 - (h % 12) - i * 3)
    events.push({
      at: isoMinutesAgo(minutesAgo),
      ap,
      rssi,
      reason: i === eventCount - 1 ? 'initial' : isLast ? 'roam' : 'roam',
    })
    minutesAgo -= 8 + ((h >> i) % 25)
  }

  return events.reverse()
}

/** Throughput model from RSSI (demo — replace with iperf/controller results in production). */
export function estimateSpeedFromRssi(rssi: number) {
  const clamped = Math.max(-90, Math.min(-45, rssi))
  const download = Math.round(Math.max(5, Math.min(450, (clamped + 90) * 5.2 + (clamped + 70) * 2)))
  const upload = Math.round(download * (0.28 + ((clamped + 80) / 100) * 0.15))
  const latencyMs = Math.round(4 + Math.max(0, -62 - clamped) * 1.8)
  const jitterMs = Math.round(1 + Math.max(0, -68 - clamped) * 0.35)
  const packetLossPct = Number(Math.max(0, Math.min(3, (-clamped - 55) * 0.08)).toFixed(1))
  return { downloadMbps: download, uploadMbps: upload, latencyMs, jitterMs, packetLossPct }
}

export function simulateClientSpeedTest(rssi: number, delayMs = 2200): Promise<ClientSpeedTestResult> {
  return new Promise(resolve => {
    window.setTimeout(() => {
      const speeds = estimateSpeedFromRssi(rssi)
      resolve({
        at: new Date().toISOString(),
        ...speeds,
      })
    }, delayMs)
  })
}

export function formatRoamingTime(iso: string) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

export function demoClientIp(clientId: string) {
  const h = Math.abs(hashStr(clientId))
  return `10.42.${(h >> 8) & 0xff}.${h & 0xff}`
}

export function demoConnectedSince(clientId: string) {
  const mins = 15 + (Math.abs(hashStr(clientId + ':since')) % 480)
  return isoMinutesAgo(mins)
}
